import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { resources } from "@constants/hardcoded-strings";
import { Company, RelyingParty } from "@idw-types/data/reference-data/generic";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { WorkflowConfiguration } from "@idw-types/data/workflow-configuration";
import { OrganizationSelectFormGroup } from "../../../common/select-form-groups";
import { SelectOption } from "@idw-types/common";
import { SearchFormFilterRow } from "../../../common/search-form-filter-row/search-form-filter-row";
import { SelectFormGroup } from "../../../common/select-form-group/select-form-group";
import { RadioInputFormGroup } from "../../../common/radio-input-form-group/radio-input-form-group";
import { TimeRangeSearchFormFilterRow } from "../../../common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { TimeRangeTypes } from "@enums/time-range-types";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { MONTH_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { UserSummarySearchFiltersState } from "@idw-types/state/user-summary-state";
import { CustomizeBySearchTypes } from "@enums/customize-by-search-types";
import { mapEntitiesWithNameAndIdToSelectOptions, mapEntityWithNameAndIdToSelectOption, mapRpServicesToSelectOptions, mapRpServiceToSelectOption } from "@utils/select-option-mapping-util";
import Button from "react-bootstrap/Button";
import { initialUserSummaryState } from "@redux/reducers/user-summary-reducer";
import styles from "./user-summary-search-form.less";
import { SearchFormHeaderContainer } from "../../../common/search-form-header-container/search-form-header-container";

type UserSummarySearchFormProps = {
    requestInProgress: boolean;
    companies: Array<Company>;
    relyingParties: Array<RelyingParty>;
    rpServices: Array<RpServiceConfiguration>;
    workflows: Array<WorkflowConfiguration>;
    searchFilters: UserSummarySearchFiltersState;
    handleSearch: (searchFilters: UserSummarySearchFiltersState) => void;
};

type UserSummarySearchFormState = {
    searchType: CustomizeBySearchTypes;
    selectedCompanyOption: SelectOption;
    selectedRpServiceOption: SelectOption;
    selectedWorkflowOption: SelectOption;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
};

export const UserSummarySearchForm = (props: UserSummarySearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        companies,
        relyingParties,
        rpServices,
        workflows,
        searchFilters,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (searchFilters: UserSummarySearchFiltersState): UserSummarySearchFormState => {
        let selectedCompany = companies.find(company => searchFilters.companyId === company.id);

        if (companies.length === 1) {
            selectedCompany = companies[0];
        }

        const selectedRpService = rpServices.find(rpService => searchFilters.rpServiceId === rpService.id);
        const selectedWorkflow = workflows.find(workflow => searchFilters.workflowId === workflow.id);

        return {
            searchType: searchFilters.customizeBySearchType,
            selectedCompanyOption: selectedCompany ? mapEntityWithNameAndIdToSelectOption(selectedCompany) : null,
            selectedRpServiceOption: selectedRpService ? mapRpServiceToSelectOption(selectedRpService) : null,
            selectedWorkflowOption: selectedWorkflow ? mapEntityWithNameAndIdToSelectOption(selectedWorkflow) : null,
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<UserSummarySearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialUserSummaryState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const companyId = filterState.selectedCompanyOption?.value || null;
        const rpServiceId = filterState.searchType === CustomizeBySearchTypes.VERIFICATION_SERVICE ? (filterState.selectedRpServiceOption?.value || null) : null;
        const workflowId = filterState.searchType === CustomizeBySearchTypes.WORKFLOW ? (filterState.selectedWorkflowOption?.value || null) : null;
        handleSearch({
            customizeBySearchType: filterState.searchType,
            companyId,
            rpServiceId,
            workflowId,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            startDate: filterState.startDate,
            endDate: filterState.endDate,
        });
    };


    const handleUpdateSearchType = (newValue: string) => {
        setFilterState({
            ...filterState,
            searchType: newValue as CustomizeBySearchTypes
        });
    };

    const handleUpdateCompanyOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedCompanyOption: newValue,
            selectedRpServiceOption: null,
            selectedWorkflowOption: null
        });
    };

    const handleUpdateRpServiceOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedRpServiceOption: newValue
        });
    };

    const handleUpdateWorkflowOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedWorkflowOption: newValue
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const searchTypeOptions: Array<SelectOption> = useMemo(() => {
        return [
            { label: "Workflow", value: CustomizeBySearchTypes.WORKFLOW },
            { label: "Verification Service", value: CustomizeBySearchTypes.VERIFICATION_SERVICE }
        ];
    }, []);

    const rpServiceOptions: Array<SelectOption> = useMemo(() => {
        const validRelyingPartyIds = (filterState.selectedCompanyOption ?
            relyingParties.filter(rp => rp.companyID === filterState.selectedCompanyOption.value) :
            relyingParties
        ).map(rp => rp.id);
        const validRpServices = rpServices.filter(service => validRelyingPartyIds.includes(service.relyingPartyID));

        return mapRpServicesToSelectOptions(validRpServices).sort((a, b) => sortStrings(a.label, b.label));
    }, [filterState.selectedCompanyOption, relyingParties, rpServices]);

    const workflowOptions: Array<SelectOption> = useMemo(() => {
        const validRelyingPartyIds = (filterState.selectedCompanyOption ?
            relyingParties.filter(rp => rp.companyID === filterState.selectedCompanyOption.value) :
            relyingParties
        ).map(rp => rp.id);
        const validWorkflows = workflows
            .filter(workflow => validRelyingPartyIds.includes(workflow.relyingPartyID))
            .sort((a, b) => sortStrings(a.name, b.name));

        return mapEntitiesWithNameAndIdToSelectOptions(validWorkflows);
    }, [filterState.selectedCompanyOption, relyingParties, workflows]);

    const timeRangeTypes = [
        TimeRangeTypes.CURRENT_MONTH,
        TimeRangeTypes.LAST_MONTH,
        TimeRangeTypes.CURRENT_QUARTER,
        TimeRangeTypes.LAST_QUARTER,
        TimeRangeTypes.CURRENT_YEAR,
        TimeRangeTypes.LAST_YEAR,
        TimeRangeTypes.CUSTOM
    ];

    return (
        <Form className={styles.userSummarySearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"user-summary"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    <SearchFormFilterRow>
                        <RadioInputFormGroup
                            controlId={"user-summary-search-filters-search-type-input"}
                            automationId={"user-summary-search-filters-search-type-input"}
                            disabled={false}
                            isRequired={false}
                            label={resources.common.searchForm.filters.customizeBy.label}
                            onChange={handleUpdateSearchType}
                            options={searchTypeOptions}
                            value={filterState.searchType}
                        />
                    </SearchFormFilterRow>
                    <SearchFormFilterRow>
                        <OrganizationSelectFormGroup
                            idPrefix="user-summary"
                            axnCompanies={companies}
                            isClearable={companies.length > 1}
                            isRequired={true}
                            layout={layout}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            selectedOption={filterState.selectedCompanyOption}
                            onChange={handleUpdateCompanyOption}
                            disabled={companies.length === 1}
                        />
                        {filterState.searchType === CustomizeBySearchTypes.VERIFICATION_SERVICE && (
                            <SelectFormGroup
                                automationId={`user-summary-search-filters-service-select`}
                                controlId={`user-summary-search-filters-service-select`}
                                label={resources.common.searchForm.filters.verificationService.label}
                                ariaLabel={resources.common.searchForm.filters.verificationService.label}
                                onChange={handleUpdateRpServiceOption}
                                options={rpServiceOptions}
                                selectedOption={filterState.selectedRpServiceOption}
                                disabled={filterState.selectedCompanyOption === null}
                                isRequired={false}
                                isSearchable={true}
                                isClearable={true}
                                labelWidth={labelWidth}
                                layout={layout}
                                noOptionsMessage={() => <span className={styles.redText}>{resources.common.searchForm.filters.verificationService.noOptionsMessage}</span>}
                            />
                        )}
                        {filterState.searchType === CustomizeBySearchTypes.WORKFLOW && (
                            <SelectFormGroup
                                automationId={`user-summary-search-filters-workflow-select`}
                                controlId={`user-summary-search-filters-workflow-select`}
                                label={resources.common.searchForm.filters.workflow.label}
                                ariaLabel={resources.common.searchForm.filters.workflow.label}
                                onChange={handleUpdateWorkflowOption}
                                options={workflowOptions}
                                selectedOption={filterState.selectedWorkflowOption}
                                disabled={filterState.selectedCompanyOption === null}
                                isRequired={false}
                                isSearchable={true}
                                isClearable={true}
                                labelWidth={labelWidth}
                                layout={layout}
                                noOptionsMessage={() => <span className={styles.redText}>{resources.common.searchForm.filters.workflow.noOptionsMessage}</span>}
                            />
                        )}
                    </SearchFormFilterRow>
                    <TimeRangeSearchFormFilterRow
                        idPrefix={"user-summary-search-filters"}
                        labelWidth={labelWidth}
                        layout={layout}
                        onChangeTimeRange={handleTimeRangeChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeEndDate={handleEndDateChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_YEAR_INPUT_DATE_FORMAT}
                        startDate={filterState.startDate}
                        endDate={filterState.endDate}
                        showMonthDropdown={false}
                        showYearDropdown={false}
                        showMonthYearPicker={true}
                    />
                    <div className={styles.buttonDiv}>
                        <Button id="user-summary-search-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <ButtonWithSpinner
                            id="user-summary-search-load-button" 
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.load.label}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};